import { Flex, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import { FileCheck2Icon, FileWarningIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { GradientIcon } from '~/common/components/GradientIcon';
import { ComplianceDocumentSummary } from './types';

interface DocumentListProps {
  documents: ComplianceDocumentSummary[];
  selectedDocumentId?: string | null;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

interface GroupedDocuments {
  /** Documents with one or more open suggestions — the agent needs to review. */
  needsReview: ComplianceDocumentSummary[];
  /** Everything else: no open issues, auto-approved, or (later) resubmitted. */
  otherDocuments: ComplianceDocumentSummary[];
}

/**
 * Splits documents into the two display groups:
 * - "Needs Review": any document with one or more open suggestions.
 * - "Other Documents": everything else — documents with no open issues,
 *   auto-approved documents, and (in a later phase) resubmitted documents.
 */
export const groupDocuments = (
  documents: ComplianceDocumentSummary[]
): GroupedDocuments => {
  const needsReview: ComplianceDocumentSummary[] = [];
  const otherDocuments: ComplianceDocumentSummary[] = [];

  for (const doc of documents) {
    if (doc.openSuggestionCount > 0) {
      needsReview.push(doc);
    } else {
      otherDocuments.push(doc);
    }
  }

  return { needsReview, otherDocuments };
};

interface DocumentRowProps {
  document: ComplianceDocumentSummary;
  isSelected: boolean;
  onSelect: (ingestionDocumentId: string) => void;
}

const DocumentRow: FC<DocumentRowProps> = ({
  document,
  isSelected,
  onSelect,
}) => {
  const needsReview = document.openSuggestionCount > 0;

  return (
    <Flex
      _hover={{
        bg: isSelected ? 'compliance.selected.bg' : 'compliance.hover.bg',
      }}
      align="center"
      aria-current={isSelected ? 'true' : undefined}
      // No border — selection is conveyed by the background highlight alone.
      bg={isSelected ? 'compliance.selected.bg' : 'transparent'}
      borderRadius="md"
      cursor="pointer"
      gap={2}
      overflow="hidden"
      px={3}
      py={2}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(document.ingestionDocumentId)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(document.ingestionDocumentId);
        }
      }}
    >
      {/* Icon keeps its status treatment regardless of selection: a solid
          yellow warning for documents that need review, and a purple-gradient
          file-check for everything else. */}
      {needsReview ? (
        <Icon
          as={FileWarningIcon}
          boxSize="16px"
          color="compliance.status.warning"
          flexShrink={0}
        />
      ) : (
        <GradientIcon
          boxSize="16px"
          flexShrink={0}
          gradientThemeKey="purple"
          icon={FileCheck2Icon}
        />
      )}
      <Text
        color={
          isSelected ? 'compliance.text.primary' : 'compliance.text.secondary'
        }
        fontSize="sm"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {document.documentName}
      </Text>
    </Flex>
  );
};

interface DocumentGroupProps {
  title: string;
  documents: ComplianceDocumentSummary[];
  selectedDocumentId?: string | null;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

const DocumentGroup: FC<DocumentGroupProps> = ({
  title,
  documents,
  selectedDocumentId,
  onSelectDocument,
}) => {
  if (documents.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={1}>
      <Heading
        color="compliance.text.muted"
        fontSize="xs"
        fontWeight="medium"
        letterSpacing="wide"
        mb={1}
        px={3}
        size="headline"
        textTransform="uppercase"
      >
        {title}
      </Heading>
      {documents.map((document) => (
        <DocumentRow
          key={document.ingestionDocumentId}
          document={document}
          isSelected={selectedDocumentId === document.ingestionDocumentId}
          onSelect={onSelectDocument}
        />
      ))}
    </VStack>
  );
};

/**
 * Selectable list of documents for the agent compliance review, grouped into
 * "Needs Review" (documents with open suggestions) and "Other Documents"
 * (no open issues / auto-approved / later resubmitted). Rows are borderless and
 * highlight on selection; the leading icon keeps its status color (yellow
 * warning vs green check) whether or not the row is selected. Selecting a row
 * calls `onSelectDocument` with the document's `ingestionDocumentId`.
 */
export const DocumentList: FC<DocumentListProps> = ({
  documents,
  selectedDocumentId,
  onSelectDocument,
}) => {
  const { needsReview, otherDocuments } = useMemo(
    () => groupDocuments(documents),
    [documents]
  );

  return (
    <VStack align="stretch" spacing={5}>
      <DocumentGroup
        documents={needsReview}
        selectedDocumentId={selectedDocumentId}
        title="Needs Review"
        onSelectDocument={onSelectDocument}
      />
      <DocumentGroup
        documents={otherDocuments}
        selectedDocumentId={selectedDocumentId}
        title="Other Documents"
        onSelectDocument={onSelectDocument}
      />
    </VStack>
  );
};
